import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, Loader2, KeyRound, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/lib/seo";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const Field = ({ id, label, type, value, onChange, placeholder, icon: Icon, autoComplete }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-ink">
      {label}
    </label>
    <div className="relative mt-1.5">
      <Icon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slatey" aria-hidden="true" />
      <input
        id={id}
        name={id}
        type={type}
        required
        autoComplete={autoComplete}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={`login-${id}`}
        className="brand-ring w-full rounded-xl border border-stroke bg-white py-3 pl-10 pr-4 text-sm text-ink placeholder:text-slate-400 transition-shadow"
      />
    </div>
  </div>
);

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await login({ email, password });
    toast.success("Signed in — welcome back");
    setLoading(false);
    navigate("/dashboard");
  };

  const sso = async () => {
    setLoading(true);
    await login({ email: "owner@enterprise.co" });
    toast.success("Signed in with Enterprise");
    setLoading(false);
    navigate("/dashboard");
  };

  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      <Seo
        title="Sign In"
        description="Sign in to your Elikya Technology business account."
        path="/login"
        robots="noindex, follow"
      />
      <header className="px-5 py-5 sm:px-8">
        <Logo />
      </header>

      <main className="flex flex-1 items-center justify-center px-5 py-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md"
        >
          <div className="rounded-2xl border border-stroke bg-white p-8 shadow-float">
            <h1 className="text-2xl font-bold tracking-tight text-ink">Welcome back</h1>
            <p className="mt-1.5 text-sm text-slatey">Sign in to your business dashboard.</p>

            <button
              onClick={sso}
              disabled={loading}
              data-testid="login-sso"
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl border border-stroke bg-canvas py-3 text-sm font-semibold text-ink transition-colors hover:bg-brand-tint disabled:opacity-60"
            >
              <KeyRound className="h-4 w-4 text-brand" />
              Sign in with Enterprise
            </button>

            <div className="my-6 flex items-center gap-3">
              <span className="h-px flex-1 bg-stroke" />
              <span className="text-xs font-medium text-slatey">or</span>
              <span className="h-px flex-1 bg-stroke" />
            </div>

            <form onSubmit={submit} className="space-y-4">
              <Field id="email" label="Work Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" icon={Mail} autoComplete="email" />
              <Field id="password" label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" icon={Lock} autoComplete="current-password" />

              <div className="flex items-center justify-between">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-slatey">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    data-testid="login-remember"
                    className="h-4 w-4 rounded border-stroke text-brand accent-[#4288CE]"
                  />
                  Remember me
                </label>
                <button type="button" className="text-sm font-medium text-brand hover:text-brand-hover">
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit"
                className="group flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-brand-hover disabled:opacity-70"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Signing in…
                  </>
                ) : (
                  <>
                    Sign In <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </button>
            </form>
          </div>

          <p className="mt-6 text-center text-sm text-slatey">
            New to Elikya?{" "}
            <Link to="/signup" className="font-semibold text-brand hover:text-brand-hover">
              Create a business account
            </Link>
          </p>
          <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-slatey">
            <ShieldCheck className="h-3.5 w-3.5 text-brand" /> Protected by bank-grade security
          </p>
        </motion.div>
      </main>
    </div>
  );
};

export default Login;
